import { ComponentType } from 'react';
import { browserHistory } from 'react-router';

import { SvgIconProps } from '@material-ui/core/SvgIcon';
import { AccountTreeOutlined as IconWorkflowBuilder, Home, Security, Assessment } from '@material-ui/icons';
import { camelCase, noop, orderBy, startCase } from 'lodash';

import { ProvisioningEnum, RolloutEnum } from '@lib/resources/types';

import IconFeatureSettings from 'icons/IconFeatureSettings';
import IconIntegrationInstructionsOutlined from 'icons/IconIntegrationInstructionsOutlined';
import IconLumiOutlined from 'icons/IconLumiOutlined';
import IconPracticeResources from 'icons/IconPracticeResources';
import IconUtilities from 'icons/IconUtilities';
import { ESyncType } from 'resources';
import settings from 'screens/MissionControl/FeaturesSettingsPage/settings/settings';

export interface Tab {
  key: string;
  label: string;
  action(): undefined | void | Window | null;
  icon?: ComponentType<SvgIconProps>;
  path?: string;
  provisioning: (ProvisioningEnum | RolloutEnum)[];
  provisioningExcluded?: (ProvisioningEnum | RolloutEnum)[];
  subMenu?: Tab[];
}

export const SETTINGS_PROVISIONING = {
  home: [],
  systemHealth: {
    overview: [RolloutEnum.NewSync],
  },
  securityCenter: {
    users: [],
    groups: [],
    tokens: [],
  },
  practiceResources: {
    providers: [],
    facilities: [],
    apptTypes: [],
    specialties: [RolloutEnum.SpecialtyReferrals],
  },
  featureSettings: {
    general: settings.General.provisioning,
    communication: settings.Communication.provisioning,
    feedback: settings.Feedback.provisioning,
    forms: settings.Forms.provisioning,
    followup: settings.Followup.provisioning,
    inboundReferral: settings['Inbound Referral'].provisioning,
    insurance: settings.Insurance.provisioning,
    outboundReferral: settings['Outbound Referral'].provisioning,
    reminder: settings.Reminder.provisioning,
    scheduler: settings.Scheduler.provisioning,
    schedulerAssist: settings['Scheduler Assist'].provisioning,
    lumaBot: settings.LumaBot.provisioning,
    telehealth: settings.Telehealth.provisioning,
    waitingRoom: settings['Waiting Room'].provisioning,
    waitlist: settings.Waitlist.provisioning,
  },
  utilities: {
    branding: {
      branding: [],
      cobranding: [RolloutEnum.Cobranding],
      embeds: [RolloutEnum.EmbedBranding],
      patientApp: [],
      hostPatient: [RolloutEnum.NextyPatient],
    },
    checklistBuilder: [RolloutEnum.ChecklistTemplate],
    customActions: [ProvisioningEnum.Followup, ProvisioningEnum.Recall, ProvisioningEnum.Reminder],
    formBuilder: [ProvisioningEnum.IntakeForm, ProvisioningEnum.PrequalForm, ProvisioningEnum.SchedulerAssist],
    eligibilitySetup: [ProvisioningEnum.InsuranceVerification],
    messageLibrary: {
      messageLibrary: [ProvisioningEnum.Broadcast, ProvisioningEnum.Chat],
      broadcastLibrary: [ProvisioningEnum.Broadcast],
      chat: [ProvisioningEnum.Chat],
    },
    lumaBot: settings.LumaBot.provisioning,
    messagePartials: [],
    messageTemplates: {
      messageTemplates: [
        ProvisioningEnum.Reminder,
        ProvisioningEnum.Feedback,
        ProvisioningEnum.Waitlist,
        ProvisioningEnum.Referral,
        ProvisioningEnum.Broadcast,
      ],
      lumaBot: settings.LumaBot.provisioning,
      appointments: [ProvisioningEnum.Reminder],
      feedback: [ProvisioningEnum.Feedback],
      offers: [ProvisioningEnum.Waitlist],
      referrals: [ProvisioningEnum.Referral],
      outreach: [ProvisioningEnum.Referral],
      waitlists: [ProvisioningEnum.Waitlist],
      other: [],
    },
    patientBilling: {
      accountManagement: [ProvisioningEnum.LumaPay],
      payouts: [ProvisioningEnum.LumaPay],
      patientBilling: [ProvisioningEnum.LumaPay, ProvisioningEnum.InsuranceVerification],
      billingItems: [ProvisioningEnum.LumaPay, ProvisioningEnum.InsuranceVerification],
    },
    queueManagerBuilder: [ProvisioningEnum.QueueManager],
  },
  followupWorkflows: [RolloutEnum.NextyFollowupWorkflows],
};

const systemHealthResources = Object.values(ESyncType);

export const SETTINGS_ITEMS: Tab[] = [
  {
    key: 'home',
    icon: Home,
    label: 'Home',
    path: '/v2/settings/home',
    action: () => browserHistory.push('/v2/settings/home'),
    provisioning: SETTINGS_PROVISIONING.home,
  },
  {
    key: 'security-center',
    icon: Security,
    label: 'Security Center',
    action: noop,
    provisioning: [],
    subMenu: [
      {
        key: 'groups',
        label: 'Groups',
        path: '/v2/settings/security-center/groups',
        action: noop,
        provisioning: SETTINGS_PROVISIONING.securityCenter.groups,
      },
      {
        key: 'users',
        label: 'Users',
        path: '/v2/settings/security-center/users',
        action: noop,
        provisioning: SETTINGS_PROVISIONING.securityCenter.users,
      },
      {
        key: 'multiple-account-management',
        label: 'Multiple Account Management',
        path: '/v2/settings/security-center/multiple-account-management',
        action: noop,
        provisioning: [],
      },
      {
        key: 'tokens',
        label: 'API Tokens',
        path: '/v2/settings/security-center/tokens',
        action: noop,
        provisioning: SETTINGS_PROVISIONING.securityCenter.tokens,
      },
    ],
  },
  {
    key: 'practice-resources',
    icon: IconPracticeResources,
    label: 'Practice Resources',
    action: noop,
    provisioning: [],
    subMenu: [
      {
        key: 'appointment-types',
        label: 'Appt Types',
        path: '/v2/settings/practice-resources/appointment-types',
        action: noop,
        provisioning: SETTINGS_PROVISIONING.practiceResources.apptTypes,
      },
      {
        key: 'facilities',
        label: 'Facilities',
        path: '/v2/settings/practice-resources/facilities',
        action: noop,
        provisioning: SETTINGS_PROVISIONING.practiceResources.facilities,
      },
      {
        key: 'providers',
        label: 'Providers',
        path: '/v2/settings/practice-resources/providers',
        action: noop,
        provisioning: SETTINGS_PROVISIONING.practiceResources.providers,
      },
      {
        key: 'specialties',
        label: 'Specialties',
        path: '/v2/settings/practice-resources/specialties',
        action: noop,
        provisioning: SETTINGS_PROVISIONING.practiceResources.specialties,
      },
    ],
  },
  {
    key: 'features',
    icon: IconFeatureSettings,
    label: 'Feature Settings',
    action: noop,
    provisioning: [],
    subMenu: [
      {
        key: 'general',
        label: 'General',
        path: '/v2/settings/features/general',
        action: noop,
        provisioning: SETTINGS_PROVISIONING.featureSettings.general,
      },
      {
        key: 'communication',
        label: 'Communication',
        path: '/v2/settings/features/communication',
        action: noop,
        provisioning: SETTINGS_PROVISIONING.featureSettings.communication,
      },
      {
        key: 'feedback',
        label: 'Feedback',
        path: '/v2/settings/features/feedback',
        action: noop,
        provisioning: SETTINGS_PROVISIONING.featureSettings.feedback,
      },
      {
        key: 'forms',
        label: 'Forms',
        path: '/v2/settings/features/forms',
        action: noop,
        provisioning: SETTINGS_PROVISIONING.featureSettings.forms,
      },
      {
        key: 'followup',
        label: 'Followup',
        path: '/v2/settings/features/followup',
        action: noop,
        provisioning: SETTINGS_PROVISIONING.featureSettings.followup,
      },
      {
        key: 'inbound-referral',
        label: 'Inbound Referral',
        path: '/v2/settings/features/inbound-referral',
        action: noop,
        provisioning: SETTINGS_PROVISIONING.featureSettings.inboundReferral,
      },
      {
        key: 'insurance',
        label: 'Insurance',
        path: '/v2/settings/features/insurance',
        action: noop,
        provisioning: SETTINGS_PROVISIONING.featureSettings.insurance,
      },
      {
        key: 'outbound-referral',
        label: 'Outbound Referral',
        path: '/v2/settings/features/outbound-referral',
        action: noop,
        provisioning: SETTINGS_PROVISIONING.featureSettings.outboundReferral,
      },
      {
        key: 'reminder',
        label: 'Reminder',
        path: '/v2/settings/features/reminder',
        action: noop,
        provisioning: SETTINGS_PROVISIONING.featureSettings.reminder,
      },
      {
        key: 'scheduler',
        label: 'Scheduler',
        path: '/v2/settings/features/scheduler',
        action: noop,
        provisioning: SETTINGS_PROVISIONING.featureSettings.scheduler,
      },
      {
        key: 'scheduler-assist',
        label: 'Scheduler Assist',
        path: '/v2/settings/features/scheduler-assist',
        action: noop,
        provisioning: SETTINGS_PROVISIONING.featureSettings.schedulerAssist,
      },
      {
        key: 'lumabot',
        label: 'LumaBot',
        path: '/v2/settings/features/lumabot',
        action: noop,
        provisioning: SETTINGS_PROVISIONING.featureSettings.lumaBot,
      },
      {
        key: 'telehealth',
        label: 'Telehealth',
        path: '/v2/settings/features/telehealth',
        action: noop,
        provisioning: SETTINGS_PROVISIONING.featureSettings.telehealth,
      },
      {
        key: 'waiting-room',
        label: 'Waiting Room',
        path: '/v2/settings/features/waiting-room',
        action: noop,
        provisioning: SETTINGS_PROVISIONING.featureSettings.waitingRoom,
      },
      {
        key: 'waitlist',
        label: 'Waitlist',
        path: '/v2/settings/features/waitlist',
        action: noop,
        provisioning: SETTINGS_PROVISIONING.featureSettings.waitlist,
      },
    ],
  },
  {
    key: 'utilities',
    icon: IconUtilities,
    label: 'Utilities',
    action: noop,
    provisioning: [],
    subMenu: orderBy(
      [
        {
          key: 'broadcast-templates',
          label: 'Broadcast Templates',
          path: '/v2/settings/utilities/broadcast-templates',
          action: noop,
          provisioning: [ProvisioningEnum.Broadcast],
        },
        {
          key: 'branding',
          label: 'Branding',
          path: '/v2/settings/utilities/branding/patient-app',
          action: noop,
          provisioning: SETTINGS_PROVISIONING.utilities.branding.branding,
          subMenu: [
            {
              key: 'cobranding',
              label: 'Cobranding',
              path: '/v2/settings/utilities/branding/cobranding',
              action: noop,
              provisioning: SETTINGS_PROVISIONING.utilities.branding.cobranding,
            },
            {
              key: 'embeds',
              label: 'Embeds',
              path: '/v2/settings/utilities/branding/embeds',
              action: noop,
              provisioning: SETTINGS_PROVISIONING.utilities.branding.embeds,
            },
            {
              key: 'patient-app',
              label: 'Patient App',
              path: '/v2/settings/utilities/branding/patient-app',
              action: noop,
              provisioning: SETTINGS_PROVISIONING.utilities.branding.patientApp,
              provisioningExcluded: SETTINGS_PROVISIONING.utilities.branding.hostPatient,
            },
            {
              key: 'patient-app-refresh',
              label: 'Patient App Refresh - Enhanced (EA)',
              path: '/v2/settings/utilities/branding/patient-app-refresh',
              action: noop,
              provisioning: SETTINGS_PROVISIONING.utilities.branding.patientApp,
              provisioningExcluded: SETTINGS_PROVISIONING.utilities.branding.hostPatient,
            },
            {
              key: 'host-patient',
              label: 'Patient App',
              path: '/v2/settings/utilities/branding/host-patient',
              action: noop,
              provisioning: SETTINGS_PROVISIONING.utilities.branding.hostPatient,
            },
          ],
        },
        {
          key: 'custom-actions',
          label: 'Custom Actions',
          path: '/v2/settings/utilities/custom-actions',
          action: noop,
          provisioning: SETTINGS_PROVISIONING.utilities.customActions,
        },
        {
          key: 'checklist-builder',
          label: 'Checklist Builder',
          path: '/v2/settings/utilities/checklist-builder',
          action: noop,
          provisioning: SETTINGS_PROVISIONING.utilities.checklistBuilder,
        },
        {
          key: 'followup-workflows',
          label: 'Workflows',
          path: '/v2/settings/utilities/followup-workflows',
          action: noop,
          provisioning: [],
        },
        {
          key: 'outbound-numbers',
          label: 'Number Management',
          path: '/v2/settings/utilities/outbound-numbers',
          action: noop,
          provisioning: [],
        },
        {
          key: 'message-template-partials',
          label: 'Message Partials',
          path: '/v2/settings/utilities/message-template-partials',
          action: noop,
          provisioning: [],
        },
        {
          key: 'form-builder',
          label: 'Form Builder',
          path: '/v2/form-builder',
          action: noop,
          provisioning: SETTINGS_PROVISIONING.utilities.formBuilder,
        },
        {
          key: 'eligibility-setup',
          label: 'Eligibility Setup',
          path: '/v2/settings/utilities/eligibility-setup?tab=payer-mapping',
          action: noop,
          provisioning: SETTINGS_PROVISIONING.utilities.eligibilitySetup,
        },
        {
          key: 'lumabot-builder',
          label: 'LumaBot Builder',
          path: '/v2/settings/utilities/lumabot-builder',
          action: noop,
          provisioning: [ProvisioningEnum.LumaBot],
        },
        {
          key: 'message-library',
          label: 'Message Library',
          path: '/v2/settings/utilities/message-library/broadcast',
          action: noop,
          provisioning: SETTINGS_PROVISIONING.utilities.messageLibrary.messageLibrary,
          subMenu: [
            {
              key: 'broadcast',
              label: 'Broadcast Library',
              path: '/v2/settings/utilities/message-library/broadcast',
              action: noop,
              provisioning: SETTINGS_PROVISIONING.utilities.messageLibrary.broadcastLibrary,
            },
            {
              key: 'chat',
              label: 'Hub Library',
              path: '/v2/settings/utilities/message-library/chat',
              action: noop,
              provisioning: SETTINGS_PROVISIONING.utilities.messageLibrary.chat,
            },
          ],
        },
        {
          key: 'message-templates',
          path: '/v2/settings/utilities/message-templates/appointments',
          label: 'Message Templates',
          action: noop,
          provisioning: SETTINGS_PROVISIONING.utilities.messageTemplates.messageTemplates,
          subMenu: orderBy(
            [
              {
                key: 'appointments',
                label: 'Appt Templates',
                path: '/v2/settings/utilities/message-templates/appointments',
                action: noop,
                provisioning: SETTINGS_PROVISIONING.utilities.messageTemplates.appointments,
              },
              {
                key: 'feedback',
                label: 'Feedback Templates',
                path: '/v2/settings/utilities/message-templates/feedback',
                action: noop,
                provisioning: SETTINGS_PROVISIONING.utilities.messageTemplates.feedback,
              },
              {
                key: 'offers',
                label: 'Offer Templates',
                path: '/v2/settings/utilities/message-templates/offers',
                action: noop,
                provisioning: SETTINGS_PROVISIONING.utilities.messageTemplates.offers,
              },
              {
                key: 'referrals',
                label: 'Referral Templates',
                path: '/v2/settings/utilities/message-templates/referrals',
                action: noop,
                provisioning: SETTINGS_PROVISIONING.utilities.messageTemplates.referrals,
              },
              {
                key: 'outreach',
                label: 'Outreach Templates',
                path: '/v2/settings/utilities/message-templates/outreach',
                action: noop,
                provisioning: SETTINGS_PROVISIONING.utilities.messageTemplates.outreach,
              },
              {
                key: 'waitlists',
                label: 'Waitlist Templates',
                path: '/v2/settings/utilities/message-templates/waitlists',
                action: noop,
                provisioning: SETTINGS_PROVISIONING.utilities.messageTemplates.waitlists,
              },
              {
                key: 'lumabot',
                label: 'LumaBot Templates',
                path: '/v2/settings/utilities/message-templates/lumabot',
                action: noop,
                provisioning: [ProvisioningEnum.LumaBot],
              },
              {
                key: 'estimates',
                label: 'Estimates Templates',
                path: '/v2/settings/utilities/message-templates/estimates',
                action: noop,
                provisioning: [],
              },
              {
                key: 'other',
                label: 'Other Templates',
                path: '/v2/settings/utilities/message-templates/other',
                action: noop,
                provisioning: SETTINGS_PROVISIONING.utilities.messageTemplates.other,
              },
            ],
            'label',
          ),
        },
        {
          key: 'patient-billing',
          label: 'LumaPay',
          path: '/v2/settings/utilities/patient-billing/items',
          action: noop,
          provisioning: SETTINGS_PROVISIONING.utilities.patientBilling.patientBilling,
          subMenu: [
            {
              key: 'billing-items',
              label: 'Billing Items',
              path: '/v2/settings/utilities/patient-billing/items',
              action: noop,
              provisioning: SETTINGS_PROVISIONING.utilities.patientBilling.billingItems,
            },
            {
              key: 'payouts',
              label: 'Payouts',
              path: '/v2/settings/utilities/patient-billing/payouts',
              action: noop,
              provisioning: SETTINGS_PROVISIONING.utilities.patientBilling.payouts,
            },
            {
              key: 'lumapay-settings',
              label: 'Settings',
              path: '/v2/settings/utilities/patient-billing/lumapay-settings',
              action: noop,
              provisioning: SETTINGS_PROVISIONING.utilities.patientBilling.accountManagement,
            },
          ],
        },
        {
          key: 'third-party-integrations',
          label: 'Third-Party Integrations',
          path: '/v2/settings/utilities/third-party-integrations',
          action: noop,
          provisioning: [],
        },
        {
          key: 'queue-manager-builder',
          label: 'Queue Manager Builder',
          path: '/v2/settings/utilities/queue-manager-builder',
          action: noop,
          provisioning: SETTINGS_PROVISIONING.utilities.queueManagerBuilder,
        },
      ],
      'label',
    ),
  },
  {
    key: 'spark-management',
    icon: IconLumiOutlined,
    label: 'Spark Management',
    action: noop,
    provisioning: [RolloutEnum.SparkManagement],
    subMenu: [
      {
        key: 'fax-transform-automations',
        label: 'Fax Transform Automations',
        path: '/v2/settings/spark-management/fax-transform-automations',
        action: noop,
        provisioning: [RolloutEnum.FaxTransform],
      },
      {
        key: 'assistants',
        label: 'Navigator Agents',
        path: '/v2/settings/spark-management/assistants',
        action: noop,
        provisioning: ['spark-navigator' as RolloutEnum],
      },
    ],
  },
  {
    key: 'followup-workflows',
    icon: IconWorkflowBuilder,
    label: 'Workflow Builder',
    path: '/v2/settings/followup-workflows',
    action: () => browserHistory.push('/v2/settings/followup-workflows'),
    provisioning: SETTINGS_PROVISIONING.followupWorkflows,
  },
  {
    key: 'integration-health',
    icon: Assessment,
    label: 'Integration Health',
    action: () => browserHistory.push('/v2/settings/integration-health/overview'),
    provisioning: [RolloutEnum.NewSync],
    subMenu: [
      {
        key: 'overview',
        label: 'Overview',
        path: `/v2/settings/integration-health/overview`,
        action: () => browserHistory.push('/v2/settings/integration-health/overview'),
        provisioning: [RolloutEnum.NewSync],
      },
      ...orderBy(
        systemHealthResources.map((resource) => ({
          key: resource,
          label: startCase(camelCase(resource)),
          path: `/v2/settings/integration-health/${resource}`,
          action: noop,
          provisioning: [RolloutEnum.NewSync],
        })),
        'label',
      ),
    ],
  },
  {
    key: 'ehr-integration',
    icon: IconIntegrationInstructionsOutlined,
    label: 'EHR Integration',
    action: noop,
    provisioning: [],
    subMenu: [
      {
        key: 'overview',
        label: 'Overview',
        path: `/v2/settings/ehr-integration/overview`,
        action: () => browserHistory.push('/v2/settings/ehr-integration/overview'),
        provisioning: [],
      },
      ...orderBy(
        systemHealthResources.map((resource) => ({
          key: resource,
          label: startCase(camelCase(resource)),
          path: `/v2/settings/ehr-integration/${resource}`,
          action: noop,
          provisioning: [],
        })),
        'label',
      ),
    ],
  },
];
